import React, { useEffect, useState, useMemo } from "react";
import ResourcePage from "./ResourcePage";
import { APP_FULL, TL, appTitle } from "../i18n";
import { useApp } from "../context/AppContext";
import client, { apiErr } from "../lib/api";
import { Button, Input, Select, Badge } from "../components/ui";
import { Modal } from "../components/ui";
import SutaManager from "../components/SutaManager";
import AutomationCard, { CapGate } from "../components/AutomationCard";
import { CalendarClock, ArrowLeftRight, HandCoins, Wallet, FileSpreadsheet, BookOpen, ExternalLink } from "lucide-react";
import { num, money, DEP_PERIODS, DEP_METHODS, MAX_SCHEDULE_ROWS, depAmount, isDeclining, periodsElapsed, accumulatedDep, bookValue, buildSchedule } from "../lib/holdingsMath";
import { fmtSchedule } from "../components/CustomFields";
import CurrencyMasterDashboard from "../components/CurrencyMasterDashboard";
import PegToMasterDashboard from "../components/PegToMasterDashboard";
import CurrencyConversionBoard from "../components/CurrencyConversionBoard";
import TransferAutomation from "../components/TransferAutomation";
import { HOLDINGS_CATEGORIES, HOLDINGS_SUBSECTIONS, HOLDINGS_GROUP_CONFIG } from "../lib/holdingsTaxonomy";
import EquityLedger from "../components/EquityLedger";
import CurrencyLedger from "../components/CurrencyLedger";
import CurrencidaStatements from "../components/CurrencidaStatements";
import NoteLedger, { NotesCategorySummary } from "../components/NoteLedger";
import CryptoLedger from "../components/CryptoLedger";
import HoldingsSummary from "../components/HoldingsSummary";
import WealthDashboard from "../components/WealthDashboard";
import AiStatementReader from "../components/AiStatementReader";
import OwnershipConsolidation from "../components/OwnershipConsolidation";
import BoxAlongStatements from "../components/BoxAlongStatements";
import MobilityLedger from "../components/MobilityLedger";
import AltAssetsLedger from "../components/AltAssetsLedger";
import CategorySeeders from "../components/CategorySeeders";
import { ImportButton, NetWorthButton, LiabilitiesManager, ExportButton, RegulatoryBundleButton } from "../components/DataImport";
import { CommitmentsSummary } from "../components/CapitalCall";
import { AppraisalReminderSetting } from "../components/Appraisal";

// SEBAA presets (alphabetical benefit names; common per-head amounts)
const BENEFIT_NAME_PRESETS = [
  "Bereavement", "Child allowance", "Disabled Adult", "Disabled Youth", "Funeral Help",
  "Maternity Leave", "Paternity Leave", "Pension", "Senior Citizen", "Student allowance",
  "Surviving Spouse", "Unemployed Adult", "Unemployed Youth", "Youth allowance",
];
const PER_HEAD_PRESETS = [1200, 1400, 1600, 1800, 2000, 2200, 2400, 2600, 2800, 3000, 3200, 3400, 3600, 3800, 4000, 4500, 5000, 6000];

export function Benefits() {
  const { isUnlocked } = useApp();
  const canPay = isUnlocked("benefits") && isUnlocked("currency");
  return (
    <ResourcePage
      collection="benefits" titleKey="benefits" titleText={appTitle("benefits")} manualKey="benefits" groupKey="type" scheduledApp="benefits"
      topSlot={<><div className="flex flex-wrap items-center gap-2 mb-3" data-testid="benefits-data-tools"><ImportButton app="benefits" onDone={() => window.location.reload()} /><ExportButton coll="benefits" label={TL("Export benefits")} /></div><AutomationCard app="benefits" />{canPay && <PayGroupCard />}</>}
      quickAdd={{ label: "Add more entries", counts: [1, 5, 10], template: { name: "New benefit", amount: 0, tax: 0, txn_fee: 0, bank_charge: 0 } }}
      fields={[
        { key: "name", label: "Benefit name", type: "optionset", presets: BENEFIT_NAME_PRESETS, customKey: "benefit_names", maxCustom: 12 },
        { key: "type", label: "Type / group", type: "optionset", presets: ["Health", "Pension", "Insurance", "Education", "Housing"], customKey: "benefit_types", maxCustom: 9 },
        { key: "groups", label: "Recipient groups (comma-separated)" },
        { key: "eligibility", label: "Eligibility (age/gender/characteristic)" },
        { key: "klass", label: "Class / group" },
        { key: "pool", label: "Fund pool name" },
        { key: "funding_pool", label: "Funding pool (currency)", dynamicCollection: "currencies" },
        { key: "funding_account", label: "Funding pool (account)", type: "account", customKey: "accounts" },
        { key: "schedule", label: "Schedule", type: "schedule" },
        { key: "amount", label: "Monthly amount", type: "number" },
        { key: "tax", label: "Withholding tax %", type: "number" },
        { key: "txn_fee", label: "Transaction fee %", type: "number" },
        { key: "bank_charge", label: "Bank charge (flat)", type: "number" },
      ]}
      columns={[
        { key: "name", label: "Benefit" },
        { key: "groups", label: "Groups" },
        { key: "eligibility", label: "Eligibility" },
        { key: "klass", label: "Class" },
        { key: "funding_account", label: "Account", render: (v) => v || "—" },
        { key: "schedule", label: "Schedule", render: (v) => fmtSchedule(v) },
        { key: "amount", label: "Monthly", render: (v) => `$${num(v).toLocaleString()}` },
        { key: "tax", label: "Tax", render: (v) => `${num(v)}%` },
        { key: "txn_fee", label: "Fee", render: (v) => `${num(v)}%` },
        { key: "bank_charge", label: "Bank", render: (v) => `$${num(v)}` },
      ]}
      rowActions={canPay ? [{
        label: "Pay / Reimburse", testid: "benefit-pay", icon: <HandCoins size={14} />,
        title: (r) => `Pay / Reimburse — ${r?.name || "Benefit"}`,
        render: (r, close) => <BenefitPayPanel benefit={r} onDone={close} />,
      }] : []}
      computeStats={(rows) => {
        const monthly = rows.reduce((a, r) => a + num(r.amount), 0);
        return [
          { label: "Benefits defined", value: `${rows.length}` },
          { label: "Projected monthly", value: `$${monthly.toLocaleString()}` },
          { label: "Projected annual", value: `$${(monthly * 12).toLocaleString()}` },
          { label: "Fund pools", value: new Set(rows.map((r) => r.pool).filter(Boolean)).size },
        ];
      }}
    />
  );
}

// SEBAA — pay or schedule a benefit payout to a Familigi payee group (per-head or split total).
// Immediate runs disburse straight away from the chosen currency pool; future-dated runs are
// scheduled and executed automatically by the background scheduler when the date arrives.
const RUN_BADGE = { paid: "success", scheduled: "info", recorded: "neutral", skipped: "danger" };
function PayGroupCard() {
  const [groups, setGroups] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [runs, setRuns] = useState([]);
  const [gid, setGid] = useState("");
  const [bid, setBid] = useState("");
  const [cid, setCid] = useState("");
  const [mode, setMode] = useState("perhead");
  const [amt, setAmt] = useState("");
  const [when, setWhen] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(""); const [ok, setOk] = useState(false);
  const [tab, setTab] = useState("group");
  const [allPersons, setAllPersons] = useState([]);
  const [treesList, setTreesList] = useState([]);
  const [salaryGrades, setSalaryGrades] = useState([]);
  const [rate, setRate] = useState("Monthly");
  const [groupBy, setGroupBy] = useState("tree");
  const [unitPools, setUnitPools] = useState({});
  const [bulkMsg, setBulkMsg] = useState(""); const [bulkOk, setBulkOk] = useState(false); const [bulkBusy, setBulkBusy] = useState(false);
  const [sutaStates, setSutaStates] = useState([]);
  const [lastStateMap, setLastStateMap] = useState({});
  const [sutaOpen, setSutaOpen] = useState(null);      // unit key under review, or null
  const [sutaRows, setSutaRows] = useState([]);
  const [sutaPreview, setSutaPreview] = useState(null);
  const [sutaDraw, setSutaDraw] = useState(false);
  const [sutaMode, setSutaMode] = useState("unit");   // "unit" (bulk) | "group"
  const sutaYear = new Date().getUTCFullYear();
  const loadSuta = () => {
    client.get(`/suta/states?year=${sutaYear}`).then((r) => setSutaStates(r.data.states || [])).catch(() => {});
    client.get("/suta/last-state").then((r) => setLastStateMap(r.data.map || {})).catch(() => {});
  };
  useEffect(() => { loadSuta(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (!sutaOpen || sutaRows.length === 0) { setSutaPreview(null); return; }
    let alive = true;
    client.post("/suta/preview", { year: sutaYear, items: sutaRows.map((r) => ({ person_id: r.person_id, person_name: r.person_name, amount: r.amount, state_code: r.state_code })) })
      .then((res) => { if (alive) setSutaPreview(res.data); }).catch(() => {});
    return () => { alive = false; };
  }, [sutaOpen, sutaRows, sutaYear]);
  const load = async () => {
    try {
      const [g, b, c, r, p, t, sg] = await Promise.all([client.get("/groups"), client.get("/benefits"), client.get("/currencies"), client.get("/benefit-runs"), client.get("/persons"), client.get("/trees"), client.get("/salary-grades")]);
      setGroups(g.data || []); setBenefits(b.data || []); setCurrencies(c.data || []); setRuns(r.data || []);
      setAllPersons(p.data || []); setTreesList(t.data || []); setSalaryGrades(Array.isArray(sg.data) ? sg.data : []);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);
  const sel = groups.find((x) => x.id === gid);
  const ben = benefits.find((x) => x.id === bid);
  const cur = currencies.find((x) => x.id === cid);
  const n = sel ? (sel.member_count ?? (sel.member_ids || []).length) : 0;
  const a = num(amt);
  const perHead = mode === "perhead" ? a : (n ? a / n : 0);
  const total = mode === "perhead" ? a * n : a;
  const txnFee = total * num(ben?.txn_fee) / 100;
  const bankCharge = num(ben?.bank_charge);
  const withholding = total * num(ben?.tax) / 100;
  const fundDraw = total + txnFee + bankCharge;
  const netToPayees = total - withholding;
  const bal = cur ? num(cur.balance) : 0;
  const today = new Date().toISOString().slice(0, 10);
  const future = !!when && when > today;
  const insufficient = !future && !!cur && fundDraw > bal;
  const pickBenefit = (id) => {
    setBid(id);
    const b = benefits.find((x) => x.id === id);
    if (b) { if (!cid && b.funding_pool) setCid(b.funding_pool); if (!amt && b.amount) setAmt(String(b.amount)); }
  };
  const _doGroupPost = async (sutaMap, draw) => {
    setBusy(true); setMsg(""); setOk(false);
    try {
      const { data } = await client.post("/benefit-runs", {
        group_id: gid, group_name: sel?.name || "", kind: "payment",
        benefit_id: bid, benefit_name: ben?.name || "", currency_id: cid, currency: cur?.name || "",
        amount_mode: mode, amount: a, member_count: n, per_head: perHead, total, when, note,
        suta_states: sutaMap || {}, suta_draw: !!draw, suta_year: sutaYear,
      });
      if (data.status === "paid" && data.result) {
        const r = data.result, tu = r.topup ? ` Auto top-up +${money(r.topup.topped_up)}.` : "";
        const st = r.total_employer_tax > 0 ? ` Employer SUTA/FUTA ${r.suta_drawn ? "drawn" : "accrued"}: ${money(r.total_employer_tax)}.` : "";
        setMsg(`Paid ${r.count} member(s) of "${r.group_name}" — total ${money(r.total)}.${st} ${r.currency_name} balance now ${money(r.currency_balance)}.${tu}`);
      } else {
        setMsg(`Scheduled ${money(total)} across ${n} payee(s) for ${when}. It will pay automatically on that date.`);
      }
      setOk(true); setAmt(""); setNote(""); setWhen(""); loadSuta(); load();
      return true;
    } catch (e) { setMsg(apiErr(e)); setOk(false); return false; } finally { setBusy(false); }
  };
  const submit = () => {
    if (!gid || !bid || !cid || !a) return;
    if (sutaStates.length && !future) {
      const members = (sel?.member_ids || []).map((id) => allPersons.find((p) => p.id === id)).filter(Boolean);
      setSutaRows(members.map((p) => ({ person_id: p.id, person_name: pName(p), amount: perHead, state_code: lastStateMap[p.id] || "" })));
      setSutaMode("group"); setSutaOpen(sel?.name || "group");
      return;
    }
    _doGroupPost({}, false);
  };
  const del = async (id) => { try { await client.delete(`/benefit-runs/${id}`); load(); } catch (e) { /* noop */ } };
  // Bulk-by-schedule (payroll): group org people by schedule -> unit, resolve amount from grade + admin addition.
  const RATES = ["Weekly", "Biweekly", "Monthly", "26× per annum"];
  const round2 = (x) => Math.round((Number(x) || 0) * 100) / 100;
  const pName = (p) => [p.first, p.middle, p.last].filter(Boolean).join(" ") || "Unnamed";
  const gradeAmt = (g) => { const r = salaryGrades.find((x) => String(x.grade) === String(g)); return r ? num(r.amount) : 0; };
  const treeName = (id) => (treesList.find((t) => t.id === id) || {}).name || "—";
  const cohorts = useMemo(() => {
    const genericTreeIds = new Set(treesList.filter((t) => t.type === "generic").map((t) => t.id));
    const elig = allPersons.filter((p) => (p.attrs?.rate_of_pay || "") === rate && (p.tree_type === "generic" || genericTreeIds.has(p.tree_id)));
    const out = {};
    elig.forEach((p) => {
      const key = groupBy === "tree" ? treeName(p.tree_id) : groupBy === "branch" ? (p.branch || "Main") : (p.attrs?.principal_group || "— unassigned —");
      const amount = round2(gradeAmt(p.attrs?.salary_grade) + num(p.attrs?.admin_addition));
      (out[key] = out[key] || { members: [], total: 0 });
      out[key].members.push({ person_id: p.id, person_name: pName(p), amount, grade: p.attrs?.salary_grade });
      out[key].total = round2(out[key].total + amount);
    });
    return out;
  }, [allPersons, treesList, rate, groupBy, salaryGrades]);
  const _runUnit = async (unitKey, items, draw) => {
    const curId = unitPools[unitKey];
    if (!curId) return;
    setBulkMsg(""); setBulkOk(false);
    try {
      const { data } = await client.post("/benefit-payments/roster", {
        currency_id: curId, items, kind: "payment", label: `${rate} · ${unitKey}`,
        schedule: rate, unit: unitKey, note: `Bulk ${rate} payroll`, suta_draw: draw, suta_year: sutaYear,
      });
      const rr = data.result;
      const st = rr.total_suta > 0 ? ` SUTA ${rr.suta_drawn ? "drawn from pool" : "accrued"}: ${money(rr.total_suta)}.` : "";
      setBulkOk(true); setBulkMsg(`Paid ${rr.count} in "${unitKey}" — ${money(rr.total)}.${st} ${rr.currency_name} now ${money(rr.currency_balance)}.`);
      loadSuta(); load();
      return true;
    } catch (e) { setBulkOk(false); setBulkMsg(apiErr(e)); return false; }
  };
  const payUnit = (unitKey) => {
    const curId = unitPools[unitKey]; const c = cohorts[unitKey];
    if (!curId || !c) return;
    if (sutaStates.length === 0) {
      _runUnit(unitKey, c.members.map((m) => ({ person_id: m.person_id, person_name: m.person_name, amount: m.amount })), false);
      return;
    }
    setSutaRows(c.members.map((m) => ({ person_id: m.person_id, person_name: m.person_name, amount: m.amount, state_code: lastStateMap[m.person_id] || "" })));
    setSutaMode("unit"); setSutaOpen(unitKey);
  };
  const confirmSutaRun = async () => {
    let ok;
    if (sutaMode === "group") {
      const map = {}; sutaRows.forEach((r) => { if (r.state_code) map[r.person_id] = r.state_code; });
      ok = await _doGroupPost(map, sutaDraw);
    } else {
      ok = await _runUnit(sutaOpen, sutaRows.map((r) => ({ person_id: r.person_id, person_name: r.person_name, amount: r.amount, state_code: r.state_code })), sutaDraw);
    }
    if (ok) setSutaOpen(null);
  };
  const payAllUnits = async () => {
    const entries = Object.entries(cohorts);
    const toPay = entries.filter(([u, c]) => unitPools[u] && c.total > 0);
    const noPool = entries.filter(([u, c]) => !unitPools[u] && c.total > 0).map(([u]) => u);
    if (!toPay.length) { setBulkOk(false); setBulkMsg("Assign a pool to at least one unit first."); return; }
    setBulkBusy(true); setBulkMsg("");
    let units = 0, people = 0, total = 0, suta = 0; const skipped = [];
    for (const [u, c] of toPay) {
      try {
        const { data } = await client.post("/benefit-payments/roster", {
          currency_id: unitPools[u],
          items: c.members.map((m) => ({ person_id: m.person_id, person_name: m.person_name, amount: m.amount, state_code: lastStateMap[m.person_id] || "" })),
          kind: "payment", label: `${rate} · ${u}`, schedule: rate, unit: u, note: `Bulk ${rate} payroll`, suta_draw: sutaDraw, suta_year: sutaYear,
        });
        units++; people += data.result.count; total = round2(total + data.result.total); suta = round2(suta + (data.result.total_suta || 0));
      } catch (e) { skipped.push(`${u} (${apiErr(e)})`); }
    }
    let m = `Paid ${units} unit(s), ${people} payee(s), total ${money(total)}.`;
    if (suta > 0) m += ` SUTA ${sutaDraw ? "drawn" : "accrued"}: ${money(suta)} (payees' remembered states).`;
    if (skipped.length) m += ` Skipped: ${skipped.join("; ")}.`;
    if (noPool.length) m += ` No pool set: ${noPool.join(", ")}.`;
    setBulkOk(skipped.length === 0); setBulkMsg(m); setBulkBusy(false); loadSuta(); load();
  };
  const downloadCsv = async (r) => {
    try {
      const { data } = await client.get(`/benefit-payments?batch_id=${r.batch_id}`);
      const head = ["Member", "Gross", "Tax", "Fee", "Bank", "Net", "Pool debit", "Date"];
      const rows = [head, ...(data || []).map((p) => [p.person_name, num(p.amount), num(p.tax_amt), num(p.fee_amt), num(p.bank_amt), num(p.net), num(p.total_debit), (p.date || "").slice(0, 10)])];
      rows.push([]);
      rows.push([`Batch ${r.batch_id}`, r.group_name || "", r.benefit_name || "", `Total ${num(r.total)}`, `${(data || []).length} payee(s)`]);
      const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `payout_${(r.group_name || "group").replace(/\s+/g, "_")}_${(r.when || r.paid_at || "").slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
  };
  return (
    <div className="rounded-xl border border-black/10 bg-white/60 p-4 dark:border-white/10 dark:bg-white/[0.03]" data-testid="pay-group-card">
      <div className="mb-3 flex items-center gap-2">
        <HandCoins size={16} className="text-[var(--primary)]" />
        <h3 className="font-head text-base">{TL("Pay a group")}</h3>
        <Badge>{TL("SEBAA")}</Badge>
      </div>
      <div className="mb-3 flex gap-1 rounded-lg bg-black/5 p-1 dark:bg-white/5">
        <button onClick={() => setTab("group")} data-testid="paytab-group" className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition ${tab === "group" ? "bg-white shadow dark:bg-white/10" : "opacity-70 hover:opacity-100"}`}>{TL("One group")}</button>
        <button onClick={() => setTab("bulk")} data-testid="paytab-bulk" className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition ${tab === "bulk" ? "bg-white shadow dark:bg-white/10" : "opacity-70 hover:opacity-100"}`}>{TL("Bulk by schedule")}</button>
      </div>
      {tab === "group" && (<>
      <div className="grid gap-2 sm:grid-cols-2">
        <Select label={TL("Payee group (from Familigi)")} value={gid} onChange={(e) => setGid(e.target.value)} data-testid="pay-group-select">
          <option value="">{TL("— select a group —")}</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.member_count ?? (g.member_ids || []).length})</option>)}
        </Select>
        <Select label={TL("Benefit")} value={bid} onChange={(e) => pickBenefit(e.target.value)} data-testid="pay-group-benefit">
          <option value="">{TL("— select a benefit —")}</option>
          {benefits.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Select>
        <Select label={TL("Currency pool")} value={cid} onChange={(e) => setCid(e.target.value)} data-testid="pay-group-currency">
          <option value="">{TL("— select a pool —")}</option>
          {currencies.map((c) => <option key={c.id} value={c.id}>{c.name} ({money(num(c.balance))})</option>)}
        </Select>
        <Select label={TL("Amount basis")} value={mode} onChange={(e) => setMode(e.target.value)} data-testid="pay-group-mode">
          <option value="perhead">{TL("Per head (× members)")}</option>
          <option value="total">{TL("Total (split evenly)")}</option>
        </Select>
        <Input label={mode === "perhead" ? TL("Amount per head") : TL("Total amount")} type="number" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="pay-group-amount" />
        <Select label={TL("Preset amount / head")} value="" onChange={(e) => { if (e.target.value) setAmt(e.target.value); }} data-testid="pay-group-amount-preset">
          <option value="">{TL("— preset —")}</option>
          {PER_HEAD_PRESETS.map((v) => <option key={v} value={v}>{money(v)}</option>)}
        </Select>
        <Input label={TL("Run date (blank = pay now)")} type="date" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="pay-group-when" />
        <Input label={TL("Note (optional)")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="pay-group-note" />
      </div>
      {sel && a > 0 && (
        <div className="mt-2 space-y-1 rounded-md bg-[var(--primary)]/10 px-3 py-2 text-sm" data-testid="pay-group-preview">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
            <span>{n} payee(s) × {money(perHead)} = <b>{money(total)}</b>{mode === "total" ? ` (${money(perHead)} each)` : ""}</span>
            {cur && <span className={insufficient ? "font-medium text-[#DA1E28]" : "opacity-70"} data-testid="pay-group-balance">{cur.name}: {money(bal)}{insufficient ? " — insufficient" : ""}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 border-t border-[var(--primary)]/20 pt-1 text-[11px] opacity-80">
            <span>Gross {money(total)}</span>
            {txnFee > 0 && <span>+ Txn fee ({num(ben?.txn_fee)}%) {money(txnFee)}</span>}
            {bankCharge > 0 && <span>+ Bank charge {money(bankCharge)}</span>}
            <span className="font-semibold opacity-100">= Total fund draw <b data-testid="pay-group-total-cost">{money(fundDraw)}</b></span>
            {withholding > 0 && <span className="opacity-70">· Withholding ({num(ben?.tax)}%) −{money(withholding)} → net to payees {money(netToPayees)}</span>}
          </div>
        </div>
      )}
      {msg && <div className={`mt-2 rounded-md p-2 text-xs font-medium ${ok ? "bg-[#24A148]/10 text-[#0E6027] dark:text-[#6FDC8C]" : "bg-[#DA1E28]/10 text-[#DA1E28]"}`} data-testid="pay-group-msg">{msg}</div>}
      <div className="mt-3">
        <Button onClick={submit} disabled={busy || !gid || !bid || !cid || !a || n === 0 || insufficient} data-testid="pay-group-submit">
          <HandCoins size={14} /> {future ? TL("Schedule payout") : TL("Pay group now")}
        </Button>
      </div>
      </>)}
      {tab === "bulk" && (
        <div data-testid="bulk-pay">
          <div className="grid gap-2 sm:grid-cols-2">
            <Select label={TL("Pay schedule (Rate of Pay)")} value={rate} onChange={(e) => setRate(e.target.value)} data-testid="bulk-rate">
              {RATES.map((rt) => <option key={rt} value={rt}>{rt}</option>)}
            </Select>
            <Select label={TL("Group by unit")} value={groupBy} onChange={(e) => setGroupBy(e.target.value)} data-testid="bulk-groupby">
              <option value="tree">{TL("Tree")}</option>
              <option value="principal_group">{TL("Principal Group")}</option>
              <option value="branch">{TL("Branch")}</option>
            </Select>
          </div>
          {bulkMsg && <div className={`mt-2 rounded-md p-2 text-xs font-medium ${bulkOk ? "bg-[#24A148]/10 text-[#0E6027] dark:text-[#6FDC8C]" : "bg-[#DA1E28]/10 text-[#DA1E28]"}`} data-testid="bulk-msg">{bulkMsg}</div>}
          {Object.keys(cohorts).length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button onClick={payAllUnits} disabled={bulkBusy || !Object.entries(cohorts).some(([u, c]) => unitPools[u] && c.total > 0)} data-testid="bulk-pay-all"><HandCoins size={14} /> {bulkBusy ? "Paying…" : "Pay all units"}</Button>
              <span className="text-[11px] opacity-60">{TL("Runs each unit's batch from its own pool.")}</span>
              {sutaStates.length > 0 && (
                <label className="ms-auto flex items-center gap-2 text-xs font-medium" data-testid="suta-draw-toggle">
                  <input type="checkbox" checked={sutaDraw} onChange={(e) => setSutaDraw(e.target.checked)} data-testid="suta-draw-checkbox" />
                  Draw employer SUTA from pool <span className="opacity-60">(off = accrue for reporting only)</span>
                </label>
              )}
            </div>
          )}
          <div className="mt-3 space-y-2">
            {Object.keys(cohorts).length === 0 && <div className="text-xs opacity-60" data-testid="bulk-empty">{TL("No org people on the")} <b>{rate}</b> {TL("schedule. Set each person's Rate of Pay & Salary Grade in Familigi first.")}</div>}
            {Object.entries(cohorts).map(([unit, c]) => {
              const pid = unitPools[unit]; const pcur = currencies.find((x) => x.id === pid);
              const short = c.total > (pcur ? num(pcur.balance) : 0);
              return (
                <div key={unit} className="rounded-lg border border-black/10 p-3 dark:border-white/10" data-testid={`bulk-unit-${unit}`}>
                  <div className="mb-1.5 flex flex-wrap items-center gap-2">
                    <span className="font-medium">{unit}</span>
                    <Badge>{c.members.length} payee(s)</Badge>
                    <span className="text-sm opacity-70">{TL("total")} <b>{money(c.total)}</b></span>
                  </div>
                  <div className="mb-2 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] opacity-70">
                    {c.members.map((m) => <span key={m.person_id}>{m.person_name}: {money(m.amount)}{m.grade ? ` (G${m.grade})` : ""}</span>)}
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-[200px] flex-1">
                      <Select label={TL("Pool for this unit")} value={pid || ""} onChange={(e) => setUnitPools({ ...unitPools, [unit]: e.target.value })} data-testid={`bulk-pool-${unit}`}>
                        <option value="">{TL("— select a pool —")}</option>
                        {currencies.map((cc) => <option key={cc.id} value={cc.id}>{cc.name} ({money(num(cc.balance))})</option>)}
                      </Select>
                    </div>
                    {pcur && short && <span className="text-xs font-medium text-[#DA1E28]">{TL("insufficient")}</span>}
                    <Button onClick={() => payUnit(unit)} disabled={!pid || short || c.total <= 0} data-testid={`bulk-pay-${unit}`}><HandCoins size={14} /> {TL("Pay unit")}</Button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4"><SutaManager /></div>
        </div>
      )}
      <Modal open={!!sutaOpen} onClose={() => setSutaOpen(null)} title={`SUTA / FUTA review — ${sutaOpen || ""}`} testid="suta-review-modal" wide>
            <div className="space-y-3">
              <p className="text-xs opacity-70">Pick each payee's state. Employer SUTA (and FUTA, if enabled) applies to wages up to each wage base at your rate.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-black/10 text-xs opacity-60 dark:border-white/10">
                    <th className="p-2 text-start">Payee</th><th className="p-2 text-end">Gross</th>
                    <th className="p-2 text-start">State</th><th className="p-2 text-end">Taxable</th><th className="p-2 text-end">SUTA</th>
                    {sutaPreview?.futa_enabled && <th className="p-2 text-end">FUTA</th>}
                  </tr></thead>
                  <tbody>
                    {sutaRows.map((r, i) => {
                      const ln = sutaPreview?.lines?.[i];
                      return (
                        <tr key={r.person_id + i} className="border-b border-black/5 dark:border-white/5" data-testid={`suta-review-row-${i}`}>
                          <td className="p-2">{r.person_name}</td>
                          <td className="p-2 text-end">{money(r.amount)}</td>
                          <td className="p-2">
                            <select value={r.state_code} data-testid={`suta-review-state-${i}`}
                              onChange={(e) => setSutaRows((prev) => prev.map((x, j) => (j === i ? { ...x, state_code: e.target.value } : x)))}
                              className="rounded-md border border-black/15 bg-white/60 px-2 py-1 text-xs dark:border-white/15 dark:bg-black/30">
                              <option value="">— No SUTA —</option>
                              {sutaStates.map((s) => <option key={s.id} value={s.state_code}>{s.state_name} ({s.rate_pct}% · base {money(s.wage_base)})</option>)}
                            </select>
                          </td>
                          <td className="p-2 text-end opacity-70">{ln ? money(ln.taxable) : "—"}{ln?.capped ? " ⛔" : ""}</td>
                          <td className="p-2 text-end font-medium">{ln ? money(ln.suta) : "—"}</td>
                          {sutaPreview?.futa_enabled && <td className="p-2 text-end opacity-80">{ln ? money(ln.futa) : "—"}</td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-[var(--primary)]/10 px-3 py-2 text-sm">
                <span data-testid="suta-review-total">Payroll {money(sutaRows.reduce((a, r) => a + num(r.amount), 0))} · SUTA <b>{money(sutaPreview?.total_suta || 0)}</b>{sutaPreview?.futa_enabled ? <> · FUTA <b>{money(sutaPreview?.total_futa || 0)}</b></> : null} · Employer tax <b>{money(sutaPreview?.total_employer_tax || 0)}</b></span>
                <label className="flex items-center gap-2 text-xs font-medium">
                  <input type="checkbox" checked={sutaDraw} onChange={(e) => setSutaDraw(e.target.checked)} data-testid="suta-review-draw" />
                  Draw employer tax from pool {sutaDraw ? `(pool draw ${money(sutaRows.reduce((a, r) => a + num(r.amount), 0) + (sutaPreview?.total_employer_tax || 0))})` : "(accrue only)"}
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setSutaOpen(null)} data-testid="suta-review-cancel">Cancel</Button>
                <Button onClick={confirmSutaRun} disabled={bulkBusy || busy} data-testid="suta-review-confirm"><HandCoins size={14} /> Confirm & pay</Button>
              </div>
            </div>
          </Modal>
      {runs.length > 0 && (
        <div className="mt-4">
          <div className="mb-1 text-xs font-semibold opacity-60">{TL("Payout runs")}</div>
          <div className="space-y-1">
            {runs.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-2 rounded border border-black/5 px-2 py-1 text-xs dark:border-white/5" data-testid={`run-${r.id}`}>
                <Badge color={RUN_BADGE[r.status] || "neutral"}>{r.status}</Badge>
                <span className="font-medium">{r.group_name}</span>
                {r.benefit_name && <span className="opacity-60">· {r.benefit_name}</span>}
                <span className="opacity-70">{money(num(r.total))} · {r.member_count} payee(s)</span>
                {r.when && <span className="opacity-50">{r.status === "paid" ? "paid" : "on"} {r.when}</span>}
                {r.skip_reason && <span className="text-[#DA1E28]" title={r.skip_reason}>{TL("skipped")}</span>}
                {r.status === "paid" && r.batch_id && (
                  <button onClick={() => downloadCsv(r)} data-testid={`run-csv-${r.id}`} className="ms-auto flex items-center gap-1 text-[var(--primary)] hover:underline"><FileSpreadsheet size={12} /> {TL("CSV")}</button>
                )}
                <button onClick={() => del(r.id)} data-testid={`run-del-${r.id}`} className={`${r.status === "paid" && r.batch_id ? "" : "ms-auto"} opacity-60 hover:text-[#DA1E28]`}>✕</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function Currency() {
  const { isUnlocked } = useApp();
  const canTopup = isUnlocked("currency") && isUnlocked("holdings");
  const topEnabled = (v) => ["yes", "true", "1", "on"].includes(String(v).toLowerCase());
  const [pegAcct, setPegAcct] = useState(null);
  const [mdKey, setMdKey] = useState(0);
  return (
    <>
    <ResourcePage
      collection="currencies" titleKey="currency" titleText={appTitle("currency")} manualKey="currency" groupKey="type" scheduledApp="currency"
      onCreated={(rec) => setPegAcct(rec)}
      topSlot={<><div className="flex flex-wrap items-center gap-2 mb-3" data-testid="currency-data-tools"><ImportButton app="currency" onDone={() => window.location.reload()} /><ExportButton coll="currencies" label={TL("Export pools")} /><LiabilitiesManager /><ImportButton app="liabilities" label={TL("Import liabilities")} onDone={() => window.location.reload()} /><ExportButton coll="liabilities" label={TL("Export liabilities")} /></div><CurrencidaStatements scope="all" /><AutomationCard app="currency" /></>}
      legend="Pen icon to edit; book icon for register; blue bar above to add more entries"
      quickAdd={{ label: "Add more entries", counts: [1, 5, 10], template: { name: "New currency", type: "Fiat", balance: 0, rate: 1 } }}
      fields={[
        { key: "name", label: "Currency name" },
        { key: "type", label: "Type", type: "optionset", presets: ["Fiat", "Mobile money", "Token", "Stablecoin"], customKey: "currency_types", maxCustom: 4 },
        { key: "kind", label: "Account kind", options: ["Deposit", "Credit account", "Credit line", "Credit card"], default: "Deposit" },
        { key: "credit_limit", label: "Credit limit (for credit accounts)", type: "number" },
        { key: "min_payment_pct", label: "Min. payment % of owed", type: "number" },
        { key: "min_payment_floor", label: "Min. payment floor ($)", type: "number" },
        { key: "payment_due_days", label: "Payment due (days after statement)", type: "number", default: "21" },
        { key: "region", label: "Region / use" },
        { key: "balance", label: "Balance", type: "number" },
        { key: "rate", label: "Rate to base", type: "number", default: "1" },
        { key: "exchange_rate", label: "Exchange rate", type: "number" },
        { key: "target_currency", label: "Target currency", max: 24 },
        { key: "from_account", label: "From account", type: "account", customKey: "accounts" },
        { key: "to_account", label: "To account", type: "account", customKey: "accounts" },
        { key: "transfer_mode", label: "Transfer mode", options: ["Fixed amount", "Percentage", "Exchange & transfer"] },
        { key: "transfer_value", label: "Transfer amount / %", type: "number" },
        { key: "schedule", label: "Date scheduler", type: "schedule" },
        { key: "alert_low", label: "Alert if below", type: "number" },
        { key: "alert_high", label: "Alert if above", type: "number" },
        { key: "fee", label: "Monthly fee", type: "number" },
      ]}
      columns={[
        { key: "name", label: "Currency" },
        { key: "type", label: "Type" },
        { key: "kind", label: "Kind", render: (v) => (v && v !== "Deposit")
            ? <Badge color="warning">{v}</Badge>
            : <span className="opacity-40">{TL("Deposit")}</span> },
        { key: "balance", label: "Balance", render: (v) => num(v).toLocaleString() },
        { key: "rate", label: "Rate→base", render: (v) => num(v) },
        { key: "exchange_rate", label: "FX rate", render: (v, r) => v ? `${num(v)}${r.target_currency ? ` → ${r.target_currency}` : ""}` : "—" },
        { key: "schedule", label: "Scheduled", render: (v) => fmtSchedule(v) },
        { key: "fee", label: "Fee", render: (v) => `$${num(v)}` },
        { key: "topup_enabled", label: "Auto top-up", render: (v, r) => topEnabled(v)
            ? <Badge color="success">≤ {money(num(r.topup_threshold))} → +{money(num(r.topup_amount))}</Badge>
            : <span className="opacity-40">{TL("off")}</span> },
      ]}
      rowActions={[
        {
          label: "Register", testid: "currency-ledger", icon: <BookOpen size={14} />, wide: "xl",
          title: (r) => `Transaction register — ${r?.name || "Account"}`,
          render: (r) => <CurrencyLedger app="currency" accountId={r.id} />,
        },
        {
          label: "Pop out", testid: "currency-popout", icon: <ExternalLink size={14} />,
          onClick: (r) => { const u = `/ledger/currency/${r.id}`; const w = window.open(u, "_blank", "width=1100,height=800"); if (!w) window.open(u, "_blank"); },
        },
        ...(canTopup ? [{
          label: "Auto top-up", testid: "currency-topup", icon: <Wallet size={14} />,
          title: (r) => `Auto top-up — ${r?.name || "Pool"}`,
          render: (r, close) => <TopupPanel currency={r} onDone={close} />,
        }] : []),
      ]}
      computeStats={(rows) => {
        const base = rows.reduce((a, r) => a + num(r.balance) * num(r.rate), 0);
        return [
          { label: "Currencies held", value: `${rows.length}` },
          { label: "Total value (base)", value: base.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
          { label: "Monthly fees", value: `$${rows.reduce((a, r) => a + num(r.fee), 0).toLocaleString()}` },
          { label: "Auto top-up on", value: `${rows.filter((r) => topEnabled(r.topup_enabled)).length}` },
        ];
      }}
      bottomSlot={<><CurrencyConversionBoard /><TransferAutomation /><CurrencyMasterDashboard key={mdKey} /></>}
    />
    {pegAcct && (
      <PegToMasterDashboard
        account={pegAcct}
        onClose={() => setPegAcct(null)}
        onPlaced={() => { setPegAcct(null); setMdKey((k) => k + 1); }}
      />
    )}
    </>
  );
}

export function Holdings() {
  const { isUnlocked } = useApp();
  const canTransact = isUnlocked("holdings") && isUnlocked("currency");
  const scheduleAction = {
    label: "Schedule", testid: "view-schedule", icon: <CalendarClock size={14} />,
    title: (r) => `Depreciation schedule — ${r?.name || "Item"}`,
    render: (r) => <ScheduleTable r={r} />,
  };
  const transactAction = {
    label: "Transact", testid: "holding-transact", icon: <ArrowLeftRight size={14} />,
    title: (r) => `Acquire / Dispose — ${r?.name || "Item"}`,
    render: (r, close) => <TransactPanel holding={r} onDone={close} />,
  };
  const registerAction = {
    label: "Register", testid: "holding-ledger", icon: <BookOpen size={14} />, wide: "xl",
    title: (r) => `Transaction register — ${r?.name || "Item"}`,
    render: (r) => <CurrencyLedger app="holdings" accountId={r.id} regName={r.name} />,
  };
  return (
    <ResourcePage
      collection="holdings" titleKey="holdings" titleText={appTitle("holdings")} manualKey="holdings" groupKey="category" accent="#C99415"
      topSlot={<><WealthDashboard /><AiStatementReader /><OwnershipConsolidation /><div className="flex flex-wrap items-center gap-2 mb-3" data-testid="holdings-data-tools"><ImportButton app="holdings" accent="#C99415" onDone={() => window.location.reload()} /><ExportButton coll="holdings" label={TL("Export holdings")} accent="#C99415" /><LiabilitiesManager /><NetWorthButton accent="#C99415" /><RegulatoryBundleButton accent="#C99415" /><AppraisalReminderSetting /></div><CommitmentsSummary /><AutomationCard app="holdings" /></>}
      fields={[
        { key: "name", label: "Item name" },
        { key: "category", label: "Category", options: HOLDINGS_CATEGORIES, allowEmpty: true, placeholder: "— select a category —" },
        { key: "subcategory", label: "Sub-section", type: "dependent", dependsOn: "category", optionsMap: HOLDINGS_SUBSECTIONS, placeholder: "— select a sub-section —", freePlaceholder: "Optional sub-section" },
        { key: "asset_class", label: "Asset class", type: "optionset", presets: ["Real estate", "Equity", "Fixed income", "Commodity", "Art", "Crypto", "Trust", "Other"], customKey: "asset_classes", maxCustom: 8 },
        { key: "geography", label: "Geography" },
        { key: "trust", label: "Holding type", options: ["Free transacting", "Restricted", "Managed trust", "Blind trust", "Joint", "Family asset"] },
        { key: "cost", label: "Acquisition cost", type: "number" },
        { key: "fees", label: "Fees", type: "number" },
        { key: "tax", label: "Tax", type: "number" },
        { key: "gross_value", label: "Gross value", type: "number" },
        { key: "dep_type", label: "Depreciation type", options: ["None", "Flat amount", "Percentage"] },
        { key: "dep_value", label: "Depreciation amount / %", type: "number" },
        { key: "dep_method", label: "Depreciation method", options: DEP_METHODS },
        { key: "dep_period", label: "Depreciation period", options: DEP_PERIODS },
        { key: "acq_date", label: "Acquisition date", type: "date" },
        { key: "salvage", label: "Salvage value (floor)", type: "number" },
      ]}
      columns={[
        { key: "name", label: "Item" },
        { key: "subcategory", label: "Sub-section", render: (v) => v || "—" },
        { key: "asset_class", label: "Class" },
        { key: "status", label: "Status", render: (v) => v === "disposed"
            ? <Badge color="danger">{TL("disposed")}</Badge>
            : <Badge color="success">{TL("held")}</Badge> },
        { key: "gross_value", label: "Gross", render: (v) => money(num(v)) },
        { key: "_dep", label: "Deprec./period", render: (_, r) => (r.dep_type && r.dep_type !== "None")
            ? (isDeclining(r)
                ? `${num(r.dep_value)}% declining / ${r.dep_period || "period"}`
                : `${money(depAmount(r))} / ${r.dep_period || "period"}${r.dep_type === "Percentage" ? ` (${num(r.dep_value)}%)` : ""}`)
            : "—" },
        { key: "_book", label: "Book value", render: (_, r) => money(bookValue(r)) },
      ]}
      computeStats={() => []}
      statsRender={(rows) => <HoldingsSummary rows={rows} />}
      rowActions={canTransact ? [scheduleAction, registerAction, transactAction] : [scheduleAction, registerAction]}
      groupConfig={{ ...HOLDINGS_GROUP_CONFIG,
        categoryRender: (cat, items) => (
          <div className="space-y-3">
            <BoxAlongStatements category={cat} items={items} />
            {cat === "Bonds and Notes" ? <NotesCategorySummary notes={items} /> : null}
          </div>
        ),
        categoryBody: (cat, items, reload) => cat === "Cryptocurrencies" ? <CryptoLedger notes={items} reload={reload} />
          : cat === "Alternative Assets" ? <AltAssetsLedger items={items} reload={reload} />
          : cat === "Mobility Property" ? <MobilityLedger items={items} reload={reload} />
          : cat === "Real Estate" ? <MobilityLedger items={items} reload={reload} category="Real Estate" class2Options={["AirBnB", "Hotel", "Multifamily", "Resort", "VRBO"]} intents={["Sell", "Hold", "Develop", "Lease", "Run"]} madeLabel="Built" p="re" />
          : ["Land", "Options", "Trusts and Tangents"].includes(cat) ? <CategorySeeders category={cat} items={items} reload={reload} /> : null,
        subRender: (cat, sub, items, reload) =>
        cat === "Equities" ? <EquityLedger sector={sub} positions={items} reload={reload} />
          : cat === "Bonds and Notes" ? <NoteLedger sector={sub} notes={items} reload={reload} />
          : null }}
    />
  );
}

function ScheduleTable({ r }) {
  if (!r.dep_type || r.dep_type === "None") {
    return <div className="py-4 text-sm opacity-60" data-testid="schedule-none">{TL("No depreciation is configured for this item.")}</div>;
  }
  if (!r.acq_date) {
    return <div className="py-4 text-sm opacity-60" data-testid="schedule-no-date">{TL("Set an acquisition date to generate the schedule.")}</div>;
  }
  const { rows, total, capped, elapsed } = buildSchedule(r);
  return (
    <div className="space-y-3" data-testid="schedule-content">
      <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
        <div><div className="text-xs opacity-60">{TL("Method")}</div><div className="font-medium">{isDeclining(r) ? "Declining balance" : "Straight-line"}</div></div>
        <div><div className="text-xs opacity-60">Per {(r.dep_period || "period").toLowerCase()}</div><div className="font-medium">{isDeclining(r) ? `${num(r.dep_value)}%` : money(depAmount(r))}</div></div>
        <div><div className="text-xs opacity-60">{TL("Periods elapsed")}</div><div className="font-medium">{elapsed}×</div></div>
        <div><div className="text-xs opacity-60">{TL("Current book value")}</div><div className="font-medium">{money(bookValue(r))}</div></div>
      </div>
      <div className="max-h-80 overflow-y-auto rounded-md border border-black/10 dark:border-white/10">
        <table className="w-full text-sm" data-testid="schedule-table">
          <thead className="sticky top-0 bg-white/90 dark:bg-black/80 backdrop-blur">
            <tr className="border-b border-black/10 dark:border-white/10">
              <th className="p-2 text-start font-medium opacity-70">#</th>
              <th className="p-2 text-end font-medium opacity-70">{TL("Opening")}</th>
              <th className="p-2 text-end font-medium opacity-70">{TL("Depreciation")}</th>
              <th className="p-2 text-end font-medium opacity-70">{TL("Closing")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.period} className={`border-b border-black/5 dark:border-white/5 ${row.elapsed ? "" : "opacity-50"}`} data-testid={`schedule-row-${row.period}`}>
                <td className="p-2">{row.period}{row.elapsed ? "" : " (proj.)"}</td>
                <td className="p-2 text-end">{money(row.opening)}</td>
                <td className="p-2 text-end text-[#DA1E28]">−{money(row.dep)}</td>
                <td className="p-2 text-end font-medium">{money(row.closing)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs opacity-60">
        {capped ? `Showing first ${MAX_SCHEDULE_ROWS} of ${total} periods.` : `${rows.length} period${rows.length === 1 ? "" : "s"} until salvage floor (${money(num(r.salvage))}).`}
        {" "}Rows past the current date are projected.
      </div>
    </div>
  );
}

function LedgerPanel({ kinds, defaults, fetchUrl, postUrl, item, onDone, currencyField = "currency_id", extraFields = [] }) {
  const [currencies, setCurrencies] = useState([]);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState(defaults);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const loadHistory = () => client.get(fetchUrl).then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => {
    client.get("/currencies").then((r) => setCurrencies(r.data.map((x) => ({ id: x.id, name: x.name, balance: x.balance })))).catch(() => {});
    loadHistory();
    // eslint-disable-next-line
  }, []);

  const submit = async () => {
    setMsg(""); setBusy(true);
    try {
      const { data } = await client.post(postUrl, { ...form, amount: parseFloat(form.amount) || 0 });
      const extra = data.topup ? ` Auto top-up: +${money(data.topup.topped_up)} from ${data.topup.source}.` : "";
      setMsg(`Recorded. Pool balance now ${money(data.currency_balance)}.${extra}`);
      setForm({ ...form, amount: "", note: "" });
      loadHistory();
    } catch (e) { setMsg(apiErr(e)); }
    setBusy(false);
  };

  const exportHistory = () => {
    const hasBreak = history.some((h) => num(h.tax_amt) || num(h.fee_amt) || num(h.bank_amt));
    const cols = ["Date", "Kind", "Currency", "Recipient", "Gross", "Tax withheld", "Fee", "Bank charge", "Net to recipient", "Pool debit", "Balance after", "Note"];
    const rows = history.map((h) => [
      (h.date || "").slice(0, 10), h.kind || "", h.currency_name || "", h.person_name || "",
      num(h.amount), num(h.tax_amt), num(h.fee_amt), num(h.bank_amt),
      (h.net != null ? num(h.net) : num(h.amount)),
      (h.total_debit != null ? num(h.total_debit) : num(h.amount)),
      (h.currency_balance_after != null ? num(h.currency_balance_after) : ""), (h.note || "").replace(/"/g, '""'),
    ]);
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const title = `${item?.name || "ledger"} — statement`;
    const lines = [[title], hasBreak ? cols : cols.filter((c) => !["Tax withheld", "Fee", "Bank charge", "Net to recipient", "Pool debit"].includes(c)), ...rows.map((r) => hasBreak ? r : r.filter((_, i) => ![5, 6, 7, 8, 9].includes(i)))];
    const csv = lines.map((r) => r.map(esc).join(",")).join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `${title.replace(/\s+/g, "_")}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="ledger-panel">
      <div className="grid grid-cols-2 gap-3">
        <Select label={TL("Action")} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="ledger-kind">
          {kinds.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
        </Select>
        <Select label={TL("Currency pool")} value={form[currencyField]} onChange={(e) => setForm({ ...form, [currencyField]: e.target.value })} data-testid="ledger-currency">
          <option value="">{TL("— select —")}</option>
          {currencies.map((c) => <option key={c.id} value={c.id}>{c.name} ({money(num(c.balance))})</option>)}
        </Select>
        {extraFields.map((ef) => (
          <Select key={ef.key} label={ef.label} value={form[ef.key]} onChange={(e) => setForm({ ...form, [ef.key]: e.target.value })} data-testid={`ledger-${ef.key}`}>
            <option value="">{ef.placeholder || "— none —"}</option>
            {ef.options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </Select>
        ))}
        <Input label={TL("Amount")} type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="ledger-amount" />
        <Input label={TL("Date")} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="ledger-date" />
        <Input label={TL("Terms / note")} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="ledger-note" />
      </div>
      {msg && <div className="rounded-md bg-black/5 dark:bg-white/10 p-2 text-sm" data-testid="ledger-msg">{msg}</div>}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>{TL("Close")}</Button>
        <Button onClick={submit} disabled={busy || !form[currencyField] || !form.amount} data-testid="ledger-submit">{TL("Record")}</Button>
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <div className="text-xs font-medium uppercase tracking-wide opacity-60">{TL("History")}</div>
          {history.length > 0 && item?.id && (
            <button onClick={exportHistory} className="text-xs text-[var(--primary)] hover:underline inline-flex items-center gap-1" data-testid="ledger-export-csv">
              <FileSpreadsheet size={13} /> Export CSV
            </button>
          )}
        </div>
        {history.length === 0 && <div className="text-sm opacity-50">{TL("No transactions yet.")}</div>}
        {history.map((h) => (
          <div key={h.id} className="border-b border-black/5 dark:border-white/5 py-1.5 text-sm" data-testid={`ledger-row-${h.id}`}>
            <div className="flex items-center justify-between">
              <span><Badge color={h.kind === "acquire" || h.kind === "payment" ? "danger" : "success"}>{h.kind}</Badge> {money(num(h.amount))} · {h.currency_name}{h.person_name ? ` · ${h.person_name}` : ""}</span>
              <span className="opacity-60">{(h.date || "").slice(0, 10)}</span>
            </div>
            {(num(h.tax_amt) || num(h.fee_amt) || num(h.bank_amt)) > 0 && (
              <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs opacity-70" data-testid={`ledger-breakdown-${h.id}`}>
                <span>gross {money(num(h.amount))}</span>
                {num(h.tax_amt) > 0 && <span>− tax {money(num(h.tax_amt))}</span>}
                {num(h.fee_amt) > 0 && <span>+ fee {money(num(h.fee_amt))}</span>}
                {num(h.bank_amt) > 0 && <span>+ bank {money(num(h.bank_amt))}</span>}
                {h.net != null && <span className="font-medium">→ net {money(num(h.net))}</span>}
                {h.total_debit != null && <span>· pool −{money(num(h.total_debit))}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TransactPanel({ holding, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <LedgerPanel
      kinds={[{ v: "acquire", l: "Acquire (draw down currency)" }, { v: "dispose", l: "Dispose (add to currency)" }]}
      defaults={{ kind: "acquire", currency_id: "", holding_id: holding.id, amount: holding.gross_value || "", date: today, note: "" }}
      fetchUrl={`/transactions?holding_id=${holding.id}`}
      postUrl="/transactions"
      item={holding}
      onDone={onDone}
    />
  );
}

function BenefitPayPanel({ benefit, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [tab, setTab] = useState("single");
  const [persons, setPersons] = useState([]);
  useEffect(() => {
    client.get("/persons").then((r) => setPersons(r.data.map((p) => ({ id: p.id, label: [p.first, p.last].filter(Boolean).join(" ") || "Unnamed" })))).catch(() => {});
  }, []);
  const tabCls = (t) => `flex-1 rounded px-3 py-1.5 text-sm ${tab === t ? "bg-[var(--primary)] text-white" : ""}`;
  return (
    <div className="space-y-4">
      <div className="flex rounded-md border border-black/15 dark:border-white/15 p-0.5" data-testid="benefit-pay-tabs">
        <button onClick={() => setTab("single")} className={tabCls("single")} data-testid="pay-tab-single">{TL("One recipient")}</button>
        <button onClick={() => setTab("group")} className={tabCls("group")} data-testid="pay-tab-group">{TL("Pay a group")}</button>
      </div>
      {tab === "single" ? (
        <LedgerPanel
          kinds={[{ v: "payment", l: "Payment (draw from pool)" }, { v: "reimbursement", l: "Reimbursement (draw from pool)" }]}
          defaults={{ kind: "payment", currency_id: benefit.funding_pool || "", benefit_id: benefit.id, person_id: "", amount: benefit.amount || "", date: today, note: "" }}
          fetchUrl={`/benefit-payments?benefit_id=${benefit.id}`}
          postUrl="/benefit-payments"
          item={benefit}
          onDone={onDone}
          extraFields={[{ key: "person_id", label: "Recipient (optional)", placeholder: "— none —", options: persons }]}
        />
      ) : (
        <GroupPayPanel benefit={benefit} onDone={onDone} />
      )}
    </div>
  );
}

function GroupPayPanel({ benefit, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [groups, setGroups] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [form, setForm] = useState({ kind: "payment", benefit_id: benefit.id, group_id: "", currency_id: benefit.funding_pool || "", amount: benefit.amount || "", date: today, note: "" });
  const [msg, setMsg] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    client.get("/groups").then((r) => setGroups(r.data || [])).catch(() => {});
    client.get("/currencies").then((r) => setCurrencies(r.data.map((x) => ({ id: x.id, name: x.name, balance: x.balance })))).catch(() => {});
  }, []);
  const group = groups.find((g) => g.id === form.group_id);
  const memberCount = group ? (group.member_count ?? (group.member_ids || []).length) : 0;
  const cur = currencies.find((c) => c.id === form.currency_id);
  const per = parseFloat(form.amount) || 0;
  const total = per * memberCount;
  const bal = cur ? num(cur.balance) : 0;
  const insufficient = !!cur && total > bal;
  const submit = async () => {
    setMsg(""); setOk(false); setBusy(true);
    try {
      const { data } = await client.post("/benefit-payments/batch", { ...form, amount: per });
      const extra = data.topup ? ` Auto top-up: +${money(data.topup.topped_up)} from ${data.topup.source}.` : "";
      const dd = data.deductions || {};
      const ded = (dd.tax || dd.fee || dd.bank)
        ? ` Per recipient: net ${money(dd.net)} (withheld tax ${money(dd.tax)}, fee ${money(dd.fee)}, bank ${money(dd.bank)}); pool debit ${money(data.per_person_debit)}/person.`
        : "";
      setMsg(`Paid ${data.count} member(s) of “${data.group_name}” — total ${money(data.total)}. ${cur?.name || "Pool"} balance now ${money(data.currency_balance)}.${ded}${extra}`);
      setOk(true); setForm({ ...form, group_id: "" });
    } catch (e) { setMsg(apiErr(e)); setOk(false); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="group-pay-panel">
      <CapGate scope="fopfo" title={TL("Paying a group draws across your connected apps (FopFo).")}>
      {groups.length === 0 && (
        <div className="rounded-md bg-black/5 dark:bg-white/10 p-3 text-sm opacity-80" data-testid="group-pay-empty">
          No action groups yet. Create one in Familigi (select people → “Create group”), then come back to pay them all at once.
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Select label={TL("Kind")} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="group-pay-kind">
          <option value="payment">{TL("Payment (draw from pool)")}</option>
          <option value="reimbursement">{TL("Reimbursement (draw from pool)")}</option>
        </Select>
        <Select label={TL("Action group")} value={form.group_id} onChange={(e) => setForm({ ...form, group_id: e.target.value })} data-testid="group-pay-group">
          <option value="">{TL("— select a group —")}</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.member_count ?? (g.member_ids || []).length})</option>)}
        </Select>
        <Select label={TL("Currency pool")} value={form.currency_id} onChange={(e) => setForm({ ...form, currency_id: e.target.value })} data-testid="group-pay-currency">
          <option value="">{TL("— select —")}</option>
          {currencies.map((c) => <option key={c.id} value={c.id}>{c.name} ({money(num(c.balance))})</option>)}
        </Select>
        <Input label={TL("Amount per person")} type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="group-pay-amount" />
        <Input label={TL("Date")} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="group-pay-date" />
        <Input label={TL("Terms / note")} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="group-pay-note" />
      </div>
      {group && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-md bg-black/[0.03] dark:bg-white/[0.06] px-3 py-2 text-sm" data-testid="group-pay-summary">
          <span><b>{memberCount}</b> {TL("recipient(s)")}</span>
          <span>× {money(per)} = <b data-testid="group-pay-total">{money(total)}</b></span>
          {cur && <span className={insufficient ? "text-[#DA1E28]" : "opacity-70"} data-testid="group-pay-balance">{cur.name}: {money(bal)}{insufficient ? " — insufficient" : ""}</span>}
        </div>
      )}
      {msg && <div className={`rounded-md p-2 text-sm ${ok ? "bg-[#24A148]/10 text-[#0E6027] dark:text-[#6FDC8C]" : "bg-[#DA1E28]/10 text-[#DA1E28]"}`} data-testid="group-pay-msg">{msg}</div>}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>{TL("Close")}</Button>
        <Button onClick={submit} disabled={busy || !form.group_id || !form.currency_id || !per || memberCount === 0 || insufficient} data-testid="group-pay-submit">
          <HandCoins size={15} /> Pay group{memberCount ? ` (${memberCount})` : ""}
        </Button>
      </div>
      </CapGate>
    </div>
  );
}


function TopupPanel({ currency, onDone }) {
  const [holdings, setHoldings] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const enabledInit = ["yes", "true", "1", "on"].includes(String(currency.topup_enabled).toLowerCase());
  const [form, setForm] = useState({
    topup_enabled: enabledInit ? "Yes" : "No",
    topup_threshold: currency.topup_threshold ?? "",
    topup_amount: currency.topup_amount ?? "",
    topup_source: currency.topup_source || "",
    topup_high: currency.topup_high ?? "",
    notify_email: currency.notify_email || "",
    notify_phone: currency.notify_phone || "",
    notify_channel: currency.notify_channel || "both",
    quiet_start: currency.quiet_start || "",
    quiet_end: currency.quiet_end || "",
  });
  const [msg, setMsg] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    client.get("/holdings").then((r) => setHoldings(r.data.map((x) => ({ id: x.id, name: x.name, value: x.gross_value })))).catch(() => {}).finally(() => setLoaded(true));
  }, []);
  const enabled = form.topup_enabled === "Yes";
  const src = holdings.find((h) => h.id === form.topup_source);
  const save = async () => {
    setBusy(true); setMsg(""); setOk(false);
    try {
      await client.put(`/currencies/${currency.id}`, { data: {
        topup_enabled: enabled ? "Yes" : "No",
        topup_threshold: parseFloat(form.topup_threshold) || 0,
        topup_amount: parseFloat(form.topup_amount) || 0,
        topup_source: form.topup_source,
        topup_high: parseFloat(form.topup_high) || 0,
        notify_email: form.notify_email,
        notify_phone: form.notify_phone,
        notify_channel: form.notify_channel,
        quiet_start: form.quiet_start,
        quiet_end: form.quiet_end,
      } });
      setMsg(enabled ? "Saved. Auto top-up is ON for this pool." : "Saved. Auto top-up is OFF.");
      setOk(true);
    } catch (e) { setMsg(apiErr(e)); setOk(false); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="topup-panel">
      <CapGate scope="fopfo" title={TL("Auto top-up links BoxAlong™ to your currency pools (FopFo).")}>
      <p className="text-sm opacity-70">
        When <b>{currency.name}</b> falls below your threshold, BoxAlong™ automatically refills it by the top-up amount,
        drawing that value from the funding holding you name below. Current balance: <b>{money(num(currency.balance))}</b>.
      </p>
      {loaded && holdings.length === 0 && (
        <div className="rounded-md bg-black/5 dark:bg-white/10 p-3 text-sm opacity-80" data-testid="topup-no-holdings">
          Add an item in Holdings (BoxAlong™) first — it becomes the funding source for auto top-ups.
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Select label={TL("Auto top-up")} value={form.topup_enabled} onChange={(e) => setForm({ ...form, topup_enabled: e.target.value })} data-testid="topup-enabled">
          <option value="No">{TL("Off")}</option>
          <option value="Yes">{TL("On")}</option>
        </Select>
        <Select label={TL("Funding source (holding)")} value={form.topup_source} onChange={(e) => setForm({ ...form, topup_source: e.target.value })} data-testid="topup-source">
          <option value="">{TL("— select a holding —")}</option>
          {holdings.map((h) => <option key={h.id} value={h.id}>{h.name} ({money(num(h.value))})</option>)}
        </Select>
        <Input label={TL("Trigger when balance falls below")} type="number" value={form.topup_threshold} onChange={(e) => setForm({ ...form, topup_threshold: e.target.value })} data-testid="topup-threshold" />
        <Input label={TL("Top-up amount")} type="number" value={form.topup_amount} onChange={(e) => setForm({ ...form, topup_amount: e.target.value })} data-testid="topup-amount" />
        <Input label={TL("High-balance alert (optional)")} type="number" value={form.topup_high} onChange={(e) => setForm({ ...form, topup_high: e.target.value })} data-testid="topup-high" />
        <Input label={TL("Notify Runner (email, optional)")} type="email" value={form.notify_email} onChange={(e) => setForm({ ...form, notify_email: e.target.value })} placeholder={TL("runner@example.com")} data-testid="topup-notify-email" />
        <Input label={TL("Notify Runner (SMS phone, optional)")} type="tel" value={form.notify_phone} onChange={(e) => setForm({ ...form, notify_phone: e.target.value })} placeholder="+16135551234" data-testid="topup-notify-phone" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Select label={TL("Notify by")} value={form.notify_channel} onChange={(e) => setForm({ ...form, notify_channel: e.target.value })} data-testid="topup-notify-channel">
          <option value="both">{TL("Email + SMS")}</option>
          <option value="email">{TL("Email only")}</option>
          <option value="sms">{TL("SMS only")}</option>
          <option value="off">{TL("Off (in-app only)")}</option>
        </Select>
        <Input label={TL("Quiet hours start (UTC)")} type="time" value={form.quiet_start} onChange={(e) => setForm({ ...form, quiet_start: e.target.value })} data-testid="topup-quiet-start" />
        <Input label={TL("Quiet hours end (UTC)")} type="time" value={form.quiet_end} onChange={(e) => setForm({ ...form, quiet_end: e.target.value })} data-testid="topup-quiet-end" />
      </div>
      <p className="text-xs opacity-50">{TL("During quiet hours, SMS is held back (email + in-app alerts still post).")}</p>
      {enabled && src && (
        <div className="rounded-md bg-black/[0.03] dark:bg-white/[0.06] px-3 py-2 text-sm" data-testid="topup-summary">
          Below {money(num(form.topup_threshold))}, add {money(num(form.topup_amount))} from <b>{src.name}</b> (holds {money(num(src.value))}).
        </div>
      )}
      {msg && <div className={`rounded-md p-2 text-sm ${ok ? "bg-[#24A148]/10 text-[#0E6027] dark:text-[#6FDC8C]" : "bg-[#DA1E28]/10 text-[#DA1E28]"}`} data-testid="topup-msg">{msg}</div>}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>{TL("Close")}</Button>
        <Button onClick={save} disabled={busy || (enabled && (!form.topup_source || !(parseFloat(form.topup_amount) > 0)))} data-testid="topup-save">
          <Wallet size={15} /> Save
        </Button>
      </div>
      </CapGate>
    </div>
  );
}
